import React, { useState, useContext, useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { readViewModelValue, appendMetadataWithIndex } from 'gw-jutro-adapters-react';
import { useValidation } from 'gw-portals-validation-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { useAuthentication } from 'gw-digital-auth-react';
import { ViewModelServiceContext, ViewModelForm } from 'gw-portals-viewmodel-react';
// eslint-disable-next-line import/no-unresolved
import config from 'app-config';
import metadata from './DriverComponent.metadata.json5';
import styles from './DriverComponent.module.scss';
import classNames from 'classnames';
import { TranslatorContext } from '@jutro/locale';
import {
    Button,
    InputField,
    Icon,
    DropdownSelectField,
    Link,
    ModalNextProvider,
    DateField
} from '@jutro/components';

import messages from './DriverComponent.messages';
import moment from 'moment';
import { messages as commonMessages } from 'gw-platform-translations';

function DriverComponent(props) {
    const {
        data: driverVM,
        readOnlyFields,
        labelPosition,
        showOptional,
        path,
        id,
        index,
        onValidate,
        onValueChange,
        submissionVM,
        updateWizardData,
        isFromNewSubmission,
        showAccountHolderToggle
    } = props;
    const { isComponentValid, onValidate: setComponentValidation } = useValidation(id);
    const viewModelService = useContext(ViewModelServiceContext);
    const { LoadSaveService } = useDependencies('LoadSaveService');
    const { EndorsementService } = useDependencies('EndorsementService');
    const { authHeader, checkPermission } = useAuthentication();
    const translator = useContext(TranslatorContext);
    const [isMVRLoading, setIsMVRLoading] = useState(false);
    const [yearRange, setYearRange] = useState([]);
    const [convictionTypeListValues, setConvictionTypeListValues] = useState([]);
    const [convictionTypeMap, setConvictionTypeMap] = useState(new Map());
    const [pardonedMap, setPardonedMap] = useState(new Map());
    const formattedMetadata = useMemo(() => {
        return appendMetadataWithIndex(metadata.pageContent, index);
    }, [index]);

    useEffect(() => {
        setYearRange(driverYearFirstLicencedAvailableValues);
        if ((submissionVM.baseData.priorCarName && submissionVM.baseData.priorCarName.value.code !== "NOPRIOR") || (submissionVM.baseData.priorNumberOfYeasrs && submissionVM.baseData.priorNumberOfYeasrs.value > 0)) {
            _.set(driverVM, 'isInsured.value', true);
        }
        if((driverVM.licenseClassificationRecords.value === undefined || driverVM.licenseClassificationRecords.children.length == 0) && driverVM.value.licenseState != 'INT'){
           //   if(driverVM.licenseClassificationRecords.value === undefined || driverVM.licenseClassificationRecords.children.length == 0){
            driverVM.licenseClassificationRecords.value = [];
            const { _xCenter, _dtoName } = driverVM.licenseClassificationRecords;
            const classRecordVM = viewModelService.create({isValidationRequired: true}, _xCenter, _dtoName);
            driverVM.licenseClassificationRecords.pushElement(classRecordVM);
        }
        _.each(driverVM.licenseClassificationRecords.value, (classificationRecord) => {
            classificationRecord.isValidationRequired = true;
        });
        const driverConvictionVM = viewModelService.create(
            {},
            'pc',
            'edge.capabilities.policyjob.lob.businessauto.coverables.dto.DriverConvictionDetails'
        );
        const availableValues = driverConvictionVM.convictionType.aspects.availableValues
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        setConvictionTypeListValues(availableValues);
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [driverVM, id, onValidate, isComponentValid]);

    const setSameAsAccountHolder = (sameAsAccountHolder) => {
        if(sameAsAccountHolder){
            const accountHolder = _.clone(_.get(submissionVM, 'baseData.accountHolder.value'));
            if(accountHolder.subtype === "Person") {
                _.set(driverVM, 'person.value', accountHolder);
            } 
            _.set(driverVM, 'dateOfBirth.value', accountHolder.dateOfBirth);
            _.set(driverVM, 'licenseNumber.value', accountHolder.licenseNumber);
            _.set(driverVM, 'maritalStatus.value', accountHolder.maritalStatus);
            _.set(driverVM, 'gender.value', accountHolder.gender);
            _.set(driverVM, 'licenseStatus.value', accountHolder.licenseStatus);                   
        } else {
            _.set(driverVM, 'person.value', {});
            _.set(driverVM, 'licenseNumber.value', undefined);
            _.set(driverVM, 'dateOfBirth.value', undefined);
            _.set(driverVM, 'gender.value', undefined);
            _.set(driverVM, 'maritalStatus.value', undefined);
        }        
        _.set(driverVM, 'reationshipToInsured.value', undefined);
        _.set(driverVM, 'occupation.value', undefined);
        _.set(driverVM, 'typeOfEmployment_EY.value', undefined);
        _.set(driverVM, 'firstLicenseDate.value', undefined);
        _.set(driverVM, 'latestLicenceReinstateDate', undefined);
        _.set(driverVM, 'hasDriverTrainingCert.value', undefined);
        _.set(driverVM, 'isInsured.value', undefined);
        _.set(driverVM, 'studentAwayHome.value', undefined);
        _.set(driverVM, 'distanceAwayHome.value', undefined);
        _.set(driverVM, 'licenseClassificationRecords.value', [{isValidationRequired: true}]);
        _.set(driverVM, 'driverConvictionDetails.value', []);
        _.set(driverVM, 'driverSuspensionDetails.value', []);
        _.set(driverVM, 'priorCancelledRecords.value', []); 
        _.set(driverVM, 'sameAsAccountHolder', sameAsAccountHolder);
        
    }

    const handleValueChange = useCallback(
        (value, changedPath) => {
             _.set(driverVM, changedPath, value);
             if(!!isFromNewSubmission && changedPath == 'sameAsAccountHolder'){
                setSameAsAccountHolder(value)
             }
             const newSubmissionVMClone = viewModelService.clone(submissionVM);
             updateWizardData(newSubmissionVMClone);
        },
        [driverVM, submissionVM, updateWizardData]
    );

    const handleValueChangeForConvictionType = useCallback( async (value, index, path) => {
            const getConvictionCodeSev = await LoadSaveService.getCategoryForConviction(
                value,
                authHeader
            );
            driverVM.driverConvictionDetails.value[index].severity = getConvictionCodeSev;
            driverVM.driverConvictionDetails.value[index].code = value;
            handleValueChange(value, path);
        },
        [handleValueChange]
    );

    const handleValueChangeForLicenseDateRecord = useCallback( async (value, path) => {
            _.set(driverVM, path, value);
            let smallestDate;
            if(driverVM.licenseClassificationRecords.value.length > 1){
                  for (let j = 0;j < driverVM.licenseClassificationRecords.value.length; j++) {
                    if(driverVM.licenseClassificationRecords.value[j].licenseClass === 'G' || driverVM.licenseClassificationRecords.value[j].licenseClass === 'G1' ||
                    driverVM.licenseClassificationRecords.value[j].licenseClass === 'G2') {
                        if(smallestDate === undefined){
                            smallestDate = driverVM.licenseClassificationRecords.value[j].licenseDate;
                        }
                        if((moment(driverVM.licenseClassificationRecords.value[j].licenseDate)).isBefore(moment(smallestDate))){
                                smallestDate = driverVM.licenseClassificationRecords.value[j].licenseDate;
                        }
                    }
                  }

            } else if(driverVM.licenseClassificationRecords.value.length == 1){
                if(driverVM.licenseClassificationRecords.value[0].licenseClass === 'G' || driverVM.licenseClassificationRecords.value[0].licenseClass === 'G1' ||
                                        driverVM.licenseClassificationRecords.value[0].licenseClass === 'G2') {
                    smallestDate = driverVM.licenseClassificationRecords.value[0].licenseDate;

                }
            }
            // if(driverVM.firstLicenseDate !== undefined && driverVM.firstLicenseDate.value !==undefined && smallestDate !== undefined){
            //     if((moment(driverVM.firstLicenseDate.value)).isBefore(moment(smallestDate))){
            //         smallestDate = driverVM.firstLicenseDate.value;
            //     }
            // }
            if(smallestDate!=undefined){
               driverVM.firstLicenseDate.value = smallestDate;
            }
            const newSubmissionVMClone = viewModelService.clone(submissionVM);
            updateWizardData(newSubmissionVMClone);
        },
        [handleValueChange]
    );

    const writeStepperValue = useCallback(
        (value, changedPath) => {
            const valueToString = _.toString(value);
            handleValueChange(valueToString, changedPath);
        },
        [handleValueChange]
    );

    const driverYearFirstLicencedAvailableValues = useMemo(() => {
        const currentYear = new Date().getUTCFullYear();
        const { ageAllowedToDrive } = config.personalAutoConfig;
        const dateOfBirth = _.get(driverVM, 'dateOfBirth.value');

        const earliestYear = _.isNil(dateOfBirth)
            ? currentYear - ageAllowedToDrive
            : dateOfBirth.year + ageAllowedToDrive;

        if (earliestYear > currentYear) {
            // return an empty object instead of just an empty array so that
            // the dropdown menu will show "No options"
            return [{}];
        }

        // _.range is not inclusive. Need to add -1 to get correct years
        const yearRange = _.range(currentYear, earliestYear - 1);

        return yearRange.map(_.toString).map((year) => ({
            code: year,
            name: year
        }));
    }, [driverVM]);

    const orderMvrForDriver = useCallback(async () => {
            _.each(driverVM.licenseClassificationRecords.value, (classificationRecord) => {
                classificationRecord.isValidationRequired = false;
            });
            if(driverVM.aspects.subtreeValid){
                setIsMVRLoading(true);
                var submissionCopy = _.cloneDeep(submissionVM.value);
                submissionCopy.lobData.businessAuto.coverables.drivers = [];
                submissionCopy.lobData.businessAuto.coverables.vehicles = [];
                submissionCopy.lobData.businessAuto.coverables.vehicleDrivers = [];
                submissionCopy.lobData.businessAuto.coverables.drivers.push(driverVM.value);
                submissionCopy.lobData.businessAuto.coverables.drivers[0].isorderMVRAutoPlus = true;
                let response;
                if(isFromNewSubmission){
                    response = await LoadSaveService.orderMVRorAutoPlusForDrivers(
                        submissionCopy,
                        authHeader
                    );
                } else {
                    response = await EndorsementService.orderMVRorAutoPlusForDrivers(
                        submissionCopy,
                        authHeader
                    );
                }
                driverVM.isorderMVRAutoPlus.value = false;
                for(var i in response.lobData.businessAuto.coverables.drivers) {
                    setIsMVRLoading(false);     
                    const mvrStatus = response.lobData.businessAuto.coverables.drivers[i].mvrStatus;               
                    if(driverVM.value.licenseNumber === response.lobData.businessAuto.coverables.drivers[i].licenseNumber) {
                        ModalNextProvider.showAlert({
                            title: messages.mvrStatus,
                            message: translator({id: `typekey.MVRStatus.${mvrStatus}`}),
                            status: 'info',
                            icon: 'mi-error-outline',
                            confirmButtonText: commonMessages.ok
                        }).catch(_.noop);
                        if(response.lobData.businessAuto.coverables.drivers[i].licenseClassificationRecords === undefined) {
                            response.lobData.businessAuto.coverables.drivers[i].licenseClassificationRecords = [];
                        }
                        driverVM.publicID.value = response.lobData.businessAuto.coverables.drivers[i].publicID;
                        driverVM.fixedId.value = response.lobData.businessAuto.coverables.drivers[i].fixedId;
                        driverVM.tempID.value = undefined;
                        driverVM.mvrStatus.value = response.lobData.businessAuto.coverables.drivers[i].mvrStatus
                        driverVM.autoPlusStatus.value = response.lobData.businessAuto.coverables.drivers[i].autoPlusStatus
                        driverVM.firstLicenseDate.value = response.lobData.businessAuto.coverables.drivers[i].firstLicenseDate;
                        driverVM.licenseStatus.value = response.lobData.businessAuto.coverables.drivers[i].licenseStatus;
                        driverVM.licenseState.value = response.lobData.businessAuto.coverables.drivers[i].licenseState;
                        if(response.lobData.businessAuto.coverables.drivers[i].driverConvictionDetails === undefined) {
                            response.lobData.businessAuto.coverables.drivers[i].driverConvictionDetails = [];
                        }
                        driverVM.driverConvictionDetails.value = response.lobData.businessAuto.coverables.drivers[i].driverConvictionDetails;
                        if(response.lobData.businessAuto.coverables.drivers[i].licenseClassificationRecords === undefined) {
                            response.lobData.businessAuto.coverables.drivers[i].licenseClassificationRecords = [];
                        }
                        driverVM.licenseClassificationRecords.value = response.lobData.businessAuto.coverables.drivers[i].licenseClassificationRecords;
                        _.each(driverVM.licenseClassificationRecords.value, (classificationRecord) => {
                            classificationRecord.isValidationRequired = true;
                        });
                        if(response.lobData.businessAuto.coverables.drivers[i].priorCancelledRecords === undefined) {
                            response.lobData.businessAuto.coverables.drivers[i].priorCancelledRecords = [];
                        }
                        driverVM.priorCancelledRecords.value = response.lobData.businessAuto.coverables.drivers[i].priorCancelledRecords;
                        if(response.baseData.priorLossHistoryRecords === undefined) {
                            response.baseData.priorLossHistoryRecords = [];
                        }
                        submissionVM.baseData.priorLossHistoryRecords.value = response.baseData.priorLossHistoryRecords;                       
                    }
                }
                updateWizardData(submissionVM);
            } else {
                _.each(driverVM.licenseClassificationRecords.value, (classificationRecord) => {
                    classificationRecord.isValidationRequired = true;
                });
                ModalNextProvider.showAlert({
                    title: commonMessages.genericError,
                    message: messages.paMvrFillDetails,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
            }
        },
        [driverVM, submissionVM, updateWizardData, viewModelService, authHeader]
    );

    const updateGenderDOBFromLicenseNumber = useCallback(async () => {
           var licenseNumber = driverVM.value.licenseNumber;
           // licenseNumber = licenseNumber.replace(/-/gi,"");
           if(licenseNumber !== undefined) {

               if(licenseNumber.length > 1){
                   licenseNumber = licenseNumber[0].toUpperCase() + licenseNumber.slice(1);
                   driverVM.value.licenseNumber = licenseNumber;
               }
               licenseNumber = licenseNumber.replaceAll("-", "");
               if(licenseNumber.length === 15 &&
                   driverVM.value.licenseState !== undefined && driverVM.value.licenseState === 'ON') {
                   const response = await LoadSaveService.updateGenderDOBFromLicenseNumber(
                       licenseNumber,
                       driverVM.value.licenseState,
                       authHeader
                   );
                   if(response.gender !== undefined){
                       driverVM.value.gender = response.gender;
                   }
                   if(response.dateOfBirth !== undefined){
                       driverVM.value.dateOfBirth = response.dateOfBirth;
                   }
               }
          }
          updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService, authHeader]
    );

    const generateNewConvictionRow = useCallback(
        (event) => {
            event.preventDefault();
            if(driverVM.driverConvictionDetails.value === undefined){
                driverVM.driverConvictionDetails.value = [];
            }
            const { _xCenter, _dtoName } = driverVM.driverConvictionDetails;
            const convictionRecordVM = viewModelService.create({source:'portal_ey',isValidationRequired: true}, _xCenter, _dtoName);
            driverVM.driverConvictionDetails.pushElement(convictionRecordVM);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const generateNewClassificationRow = useCallback(
        (event) => {
            event.preventDefault();
            if(driverVM.licenseClassificationRecords.value === undefined){
                driverVM.licenseClassificationRecords.value = [];
            }
            const { _xCenter, _dtoName } = driverVM.licenseClassificationRecords;
            const classRecordVM = viewModelService.create({isValidationRequired: true}, _xCenter, _dtoName);
            driverVM.licenseClassificationRecords.pushElement(classRecordVM);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const generateNewSuspensionRow = useCallback(
        (event) => {
            event.preventDefault();
            if(driverVM.driverSuspensionDetails.value === undefined){
                driverVM.driverSuspensionDetails.value = [];
            }
            const { _xCenter, _dtoName } = driverVM.driverSuspensionDetails;
            const suspensionRecordVM = viewModelService.create({source: "portal_ey"}, _xCenter, _dtoName); // DE4912 - changes done to save suspension details from portal
            driverVM.driverSuspensionDetails.pushElement(suspensionRecordVM);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const getConvictionCellDataNew = useCallback(
        (item, index, props) => {
            let path = props.path;
            const value = item[path];
            if (path == 'code') {
                path  = `driverConvictionDetails.value[${index}].${path}`;
                return (
                    <InputField
                        dataType="string"
                        id={index + path}
                        value={value}
                        path={path}
                        readOnly={true}
                        onValueChange={handleValueChange}
                    />
                );
            }
            else if (path == 'convictionType' || path == 'severity') {
                let readyOnlyForSev = false;
                if (path == 'severity') {
                    readyOnlyForSev = true;
                }
                const availableValues = _.get(driverVM.driverConvictionDetails.children[0], `${path}.aspects.availableValues`)
                    .map((i) => {
                        const name = translator({ id: i.name });
                        return { code: i.code, name: name }
                    });
                path  = `driverConvictionDetails.value[${index}].${path}`;
                return (
                    <DropdownSelectField
                        className={styles.coverageForm}
                        id={index + path}
                        readOnly={readyOnlyForSev}
                        onValueChange={(value) => handleValueChangeForConvictionType(value, index, path)}
                        dataType="string"
                        path={path}
                        value={value}
                        availableValues={availableValues}
                        searchable={true}
                    />
                )
            }
            else if (path == 'licenseClass') {
                const availableValues = _.get(driverVM.licenseClassificationRecords.children[index], `${path}.aspects.availableValues`)
                    .map((i) => {
                        const name = translator({ id: i.name });
                        return { code: i.code, name: name }
                    });
                  path  = `licenseClassificationRecords.value[${index}].${path}`;
                return (
                    <DropdownSelectField
                        className={styles.coverageForm}
                        id={index + path}
                        onValueChange={handleValueChange}
                        dataType="string"
                        path={path}
                        value={value}
                        required={item.isValidationRequired}
                        availableValues={availableValues}
                        searchable={true}
                    />
                )
            } else if (path == 'licenseDate') {
                path  = `licenseClassificationRecords.children[${index}].${path}`;
                return (
                    <DateField
                        id={index + path}
                        value={value}
                        dataType={_.isString(value) ? 'string' : 'object'}
                        path={path}
                        format="short"
                        layout="full-width"
                        showOptional={false}
                        required={item.isValidationRequired}
                        onValueChange={handleValueChangeForLicenseDateRecord}
                        placeholder = "MM/DD/YYYY"
                    />
                );
            } else if (path == 'convictionDate') {
                path  = `driverConvictionDetails.children[${index}].${path}`;
                return (
                    <DateField
                        id={index + path}
                        value={value}
                        dataType={_.isString(value) ? 'string' : 'object'}
                        path={path}
                        format="short"
                        layout="full-width"
                        showOptional={false}
                        onValueChange={handleValueChange}
                    />
                );
            }
        },
        [driverVM]
    );

    const getPriorHistoryTableCell =  useCallback(
        (item, index, props) => {
           let path = props.path;
           const value = item[path];
            if (props.path == 'cancelledPolicyNumber') {
                path  = `priorCancelledRecords.value[${index}].${path}`;
                return (
                    <InputField
                        dataType="string"
                        id={index + path}
                        value={value}
                        path={path}
                        onValueChange={handleValueChange}
                    />
                );
            }
            else if (props.path == 'cancelledReason') {
                const availableValues = driverVM.priorCancelledRecords.children.length > 0 ? driverVM.priorCancelledRecords.children[0].cancelledReason.aspects.availableValues.map((typeCode) => {
                   return {
                           code: typeCode.code,
                           name: translator({
                               id: typeCode.name,
                               defaultMessage: typeCode.name
                           })
                       };
                   }): [];
                   path  = `priorCancelledRecords.value[${index}].${path}`;
                return (
                    <DropdownSelectField
                        className={styles.coverageForm}
                        id={index + path}
                        onValueChange={handleValueChange}
                        dataType="string"
                        path={path}
                        value={value}
                        availableValues={availableValues}
                        searchable={true}
                    />
                );
            }
            else if(props.path == 'cancelledDate') {
                path  = `priorCancelledRecords.children[${index}].${path}`;
                return (
                    <DateField
                        id={index + path}
                        value={value}
                        dataType={_.isString(value) ? 'string' : 'object'}
                        path={path}
                        format="short"
                        layout="full-width"
                        showOptional={false}
                        onValueChange={handleValueChange}
                    />
                );
            }
            else if (props.path == 'product_EY') {
                const availableValues = driverVM.priorCancelledRecords.children.length > 0 ? driverVM.priorCancelledRecords.children[0].product_EY.aspects.availableValues.map((typeCode) => {
                   return {
                           code: typeCode.code,
                           name: translator({
                               id: typeCode.name,
                               defaultMessage: typeCode.name
                           })
                       };
                   }): [];
                   path  = `priorCancelledRecords.value[${index}].${path}`;
                return (
                    <DropdownSelectField
                        className={styles.coverageForm}
                        id={index + path}
                        onValueChange={handleValueChange}
                        dataType="string"
                        path={path}
                        value={value}
                        availableValues={availableValues}
                        searchable={true}
                    />
                );
            }
        },
        [driverVM, submissionVM, updateWizardData, handleValueChange]
    );

    const generatePriorPolicyTableRow = useCallback(
        (event) => {
            event.preventDefault();
            if(driverVM.priorCancelledRecords.value === undefined){
                driverVM.priorCancelledRecords.value = [];
            }
            const { _xCenter, _dtoName } = driverVM.priorCancelledRecords;
            const classRecordVM = viewModelService.create({isValidationRequired:true}, _xCenter, _dtoName);
            driverVM.priorCancelledRecords.pushElement(classRecordVM);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const removeAssignmentItem = (index) => {
        driverVM.priorCancelledRecords.value.splice(index, 1);
        updateWizardData(submissionVM);
    }

    const removeAssignmentRow = useCallback(
        (item, index) => {
            return (
                <Button onClick={(e) => removeAssignmentItem(index, item)}>
                    <Icon icon="mi-delete" />
                </Button>
            );
        },
        [removeAssignmentItem]
    );

    const removeSuspensionRow = useCallback(
        (evt) => {
            const index = evt.index;
            driverVM.driverSuspensionDetails.value.splice(index, 1);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const removeConvictionItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            driverVM.driverConvictionDetails.value.splice(rowId, 1);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const removeConvictionRow = useCallback(
        (evt) => {
            const index = evt.index;
            driverVM.driverConvictionDetails.value.splice(index, 1);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const removeClassificationItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            driverVM.licenseClassificationRecords.value.splice(rowId, 1);
            updateWizardData(submissionVM);
        },
        [driverVM, submissionVM, updateWizardData, viewModelService]
    );

    const removeClassificationRow = useCallback(
        (item, index) => {
            return (
                <Link
                    to="/"
                    onClick={(e) => removeClassificationItem(e, index, item)}
                    className={classNames(styles.deleteSection, styles.deleteRow)}
                >
                    <Icon icon="mi-delete" className={styles.deleteIcon} />
                </Link>
            );
        },
        [removeConvictionItem]
    );

    const updateConvictionDetails = useCallback( async (value, path) => {
            var regex = /\d+/g;
            let index = path.match(regex);
            path = path.replace('customConvictionType', 'convictionType');
            setConvictionTypeMap(convictionTypeMap.set(index, value));
            const getConvictionCodeSev = await LoadSaveService.getCategoryForConviction(
                value,
                authHeader
            );
            driverVM.driverConvictionDetails.value[index].severity = getConvictionCodeSev;
            driverVM.driverConvictionDetails.value[index].code = value;
            handleValueChange(value, path);
        },
        [handleValueChange]
    );

    const updatePardonDetails = useCallback((value, path) => {
            var regex = /\d+/g;
            let index = path.match(regex);
            path = path.replace('customPardoned', 'pardoned');
            setPardonedMap(pardonedMap.set(index, value));
            handleValueChange(value, path);
        },
        [handleValueChange]
    );

    const overrideTitleProps = useCallback(() => {
        let overrideProps = {};
        if(driverVM.driverSuspensionDetails.value) {
        const suspensionOverrideProps = driverVM.driverSuspensionDetails.value.map((value, index) => {
            return {
                [`suspensionHeaderCount${index}`]: {
                   // visible: index > 0,
                    content: translator(messages.suspensionCount) + (index + 1)
                }
            };
        });

            overrideProps = Object.assign(overrideProps, ...suspensionOverrideProps);
    } /*else {return null}*/
        if(driverVM.driverConvictionDetails.value) {
            const driverConvictionVM = viewModelService.create(
                {},
                'pc',
                'edge.capabilities.policyjob.lob.businessauto.coverables.dto.DriverConvictionDetails'
            );
            const convictionTypeValues = driverConvictionVM.convictionType.aspects.availableValues
                .map((typeCode) => {
                    return {
                        code: typeCode.code,
                        name: translator({
                            id: typeCode.name,
                            defaultMessage: typeCode.name
                        })
                    };
                });
            const pardonedValues = driverConvictionVM.pardoned ? driverConvictionVM.pardoned.aspects.availableValues
                .map((typeCode) => {
                    return {
                        code: typeCode.code,
                        name: translator({
                            id: typeCode.name,
                            defaultMessage: typeCode.name
                        })
                    };
                }): [];
            const convictionOverrideProps = driverVM.driverConvictionDetails.value.map((value, index) => {
                value.isValidationRequired=true;
                return {
                    [`convictionHeaderCount${index}`]: {
                        content: translator(messages.baConvictionCount) + (index + 1)
                    },
                    [`baDeleteSuspension${index}`]: {
                        visible: value.source === "portal_ey"
                    },
                    [`customConvictionType${index}`]: {
                        //availableValues: convictionTypeListValues,
                        availableValues: convictionTypeValues,
                        onValueChange: updateConvictionDetails,
                        value: convictionTypeMap.get(index) ? convictionTypeMap.get(index) : value.convictionType,
                        //value: value.convictionType,
                        disabled: value.source !== "portal_ey",
                        required: value.isValidationRequired
                    },
                    [`customPardoned${index}`]: {
                        availableValues: pardonedValues,
                        onValueChange: updatePardonDetails,
                        value: pardonedMap.get(index) ? pardonedMap.get(index) : value.pardoned,
                        //value: value.pardoned,
                        disabled: value.source !== "portal_ey"
                    },
                    [`offenceDate${index}`]: {
                        disabled: value.source !== "portal_ey"
                    },
                    [`convictionDate${index}`]: {
                        disabled: value.source !== "portal_ey"
                    },
                };
            });

            overrideProps = Object.assign(overrideProps, ...convictionOverrideProps);
        } /*else {return null}*/
        return overrideProps;
    }, [driverVM]);

     

    const overrideProps = {
        '@field': {
            labelPosition,
            showOptional: false,
            showRequired: true
        },
        [`driverSameAsAccountHolder${index}`]:{
            visible: index == 0 && showAccountHolderToggle
        },
        [`driverFirstName${index}`]: { 
            readOnly: index == 0 ? (driverVM.sameAsAccountHolder.value) : _.includes(readOnlyFields, 'driverFirstName') 
        },
        [`driverMiddleName${index}`]: { 
            readOnly: index == 0 ? (driverVM.sameAsAccountHolder.value) : _.includes(readOnlyFields, 'driverMiddleName') 
         },
        [`driverLastName${index}`]: { 
            readOnly: index == 0 ? (driverVM.sameAsAccountHolder.value) : _.includes(readOnlyFields, 'driverLastName')
        },
        [`driverDateOfBirth${index}`]: { readOnly: _.includes(readOnlyFields, 'driverDateOfBirth') },
        [`driverGender${index}`]: { readOnly: _.includes(readOnlyFields, 'driverGender') },
        [`driverMaritalStatus${index}`]: { readOnly: _.includes(readOnlyFields, 'driverMaritalStatus') },
        [`driverLicenseNumber${index}`]: {
            readOnly: _.includes(readOnlyFields, 'driverLicenseNumber'),
            onBlur: updateGenderDOBFromLicenseNumber
        },
        [`driverLicenceState${index}`]: { readOnly: _.includes(readOnlyFields, 'driverLicenceState') },
        [`driverFirstLicenseDate{index}`]: {
            readOnly: _.includes(readOnlyFields, 'driverYearFirstLicenced'),
            availableValues: yearRange
        },
        [`convictionTable${index}`]: {
             data:  driverVM.driverConvictionDetails.value || [] ,
             visible: driverVM.driverConvictionDetails.value ? driverVM.driverConvictionDetails.value.length > 0 : false
        },
        [`classificationTable${index}`]: {
             data: driverVM.licenseClassificationRecords.value || []
        },
        [`orderMvrbtnLoader${index}`]: {
             loaded: !isMVRLoading
        },
        [`orderMvrbtnDiv${index}`]: {
             visible: !isMVRLoading && (isFromNewSubmission || !driverVM.existingDriver_EY.value)
        },
        [`priorCancelationDataTable${index}`]: {
             data:  driverVM.priorCancelledRecords.value || [] ,
             visible: driverVM.priorCancelledRecords.value ? driverVM.priorCancelledRecords.value.length > 0 : false
        },
        [`orderMvrButtonId${index}`]:{
            visible: checkPermission('accesstomvr_EY')
        },
        /*[`typeaheadsingleselect${index}`]: {
            availableValues: convictionDetails
        },*/
        [`distanceAwayFromHome${index}`]:{
            visible: driverVM.studentAwayHome.value === true ? true : false
        },
        [`temporaryFarmEmployee${index}`]:{
            /*ARF 2023 - Sets visibility of the field based flag isVehicleEffectiveFebAutoRateFiling_EY */
            visible: driverVM.isDriverEffectiveFebAutoRateFiling_EY.value ? driverVM.isDriverEffectiveFebAutoRateFiling_EY.value : submissionVM.lobData.businessAuto.coverables.isEffectiveFebAutoRateFiling_EY.value
        },
        [`isEnrolledInTelematics_EY${index}`]: {
            visible:
            submissionVM.lobData.businessAuto.coverables.isEffectiveOct24AutoRateFiling_EY.value
        },
        [`telematicsEmail${index}`]: {
            visible:
            submissionVM.lobData.businessAuto.coverables.isEffectiveOct24AutoRateFiling_EY.value
        },
        ...overrideTitleProps()
    };

    const getLicenseClassHeader = () => {
        return <div>{translator(messages.licenseClassHeader)} <span class={styles.required}>*</span></div>
    };

    const getLicenseDateHeader = () => {
        return <div>{translator(messages.licenseDateHeader)} <span class={styles.required}>*</span></div>
    }

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            getConvictionCellDataNewtest: getConvictionCellDataNew,
            generateNewConvictionTableRow: generateNewConvictionRow,
            removeConvictionTableRow: removeConvictionRow,
            generateNewSuspensionTableRow: generateNewSuspensionRow,
            onRemoveSuspensionRecord: removeSuspensionRow,
            generateNewClassificationTableRow: generateNewClassificationRow,
            removeClassificationTableRow: removeClassificationRow,
            orderMvrForDriver,
            getPriorHistoryTableCell: getPriorHistoryTableCell,
            generateNewPriorPolicyTableRow: generatePriorPolicyTableRow,
            removeAssignmentTableRow: removeAssignmentRow,
            getLicenseClassHeader: getLicenseClassHeader,
            getLicenseDateHeader: getLicenseDateHeader
        }
    };

    const readValue = useCallback(
        (fieldId, fieldPath) => {
            return readViewModelValue(
                formattedMetadata,
                driverVM,
                fieldId,
                fieldPath,
                overrideProps
            );
        },
        [driverVM, formattedMetadata, overrideProps]
    );

    return (
        <ViewModelForm
            uiProps={formattedMetadata}
            model={driverVM}
            overrideProps={overrideProps}
            onValidationChange={setComponentValidation}
            onValueChange={handleValueChange}
            resolveValue={readValue}
            callbackMap={resolvers.resolveCallbackMap}
            classNameMap={resolvers.resolveClassNameMap}
        />
    );
}

DriverComponent.propTypes = {
    data: PropTypes.shape({}),
    labelPosition: PropTypes.string,
    path: PropTypes.string,
    onValueChange: PropTypes.func.isRequired,
    readOnlyFields: PropTypes.arrayOf(PropTypes.string),
    onValidate: PropTypes.func.isRequired,
    showOptional: PropTypes.bool,
    index: PropTypes.string,
    id: PropTypes.string
};
DriverComponent.defaultProps = {
    data: {},
    labelPosition: 'left',
    path: undefined,
    readOnlyFields: [],
    showOptional: true,
    id: undefined,
    index: 0
};
export default DriverComponent;
